import { useEffect, useMemo, useState } from "react";
import {
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { buildColumnDefs } from "./columnDefs";
import { withFixedColumns } from "./tableLayout";
import { useTableLayout } from "./useTableLayout";

export const PAGE_SIZE = 30;

/**
 * Instância TanStack com layout persistido, filtros (controlados ou internos),
 * ordenação e paginação. `columns` e `data` devem ser estáveis (módulo ou useMemo).
 */
export const useDataTable = ({ tableKey, columns, data, columnFilters, onColumnFiltersChange, pageSize = PAGE_SIZE, initialSorting = [] }) => {
    const columnDefs = useMemo(() => buildColumnDefs(columns), [columns]);
    const { layout, onColumnOrderChange, onColumnVisibilityChange, onColumnSizingChange, resetLayout } = useTableLayout(tableKey, columns);
    const [internalFilters, setInternalFilters] = useState([]);
    const filters = columnFilters ?? internalFilters;
    const setFilters = onColumnFiltersChange ?? setInternalFilters;
    const [sorting, setSorting] = useState(initialSorting);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });
    const columnOrder = useMemo(() => withFixedColumns(columns, layout.columnOrder), [columns, layout.columnOrder]);

    const filtersKey = JSON.stringify(filters);
    useEffect(() => {
        setPagination((current) => (current.pageIndex === 0 ? current : { ...current, pageIndex: 0 }));
    }, [filtersKey]);

    const table = useReactTable({
        data,
        columns: columnDefs,
        state: {
            columnFilters: filters,
            sorting,
            pagination,
            columnOrder,
            columnVisibility: layout.columnVisibility,
            columnSizing: layout.columnSizing,
        },
        onColumnFiltersChange: setFilters,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onColumnOrderChange,
        onColumnVisibilityChange,
        onColumnSizingChange,
        columnResizeMode: "onEnd",
        autoResetPageIndex: false,
        getRowId: (row, index) => (row?._id ? String(row._id) : String(index)),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    const pageCount = table.getPageCount();
    useEffect(() => {
        if (pageCount > 0 && pagination.pageIndex > pageCount - 1) {
            setPagination((current) => ({ ...current, pageIndex: pageCount - 1 }));
        }
    }, [pageCount, pagination.pageIndex]);

    return { table, resetLayout };
};
